import { useEffect, useState } from "react";
import { list, update, remove, create } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EventSelect from "@/components/EventSelect";
import { PriorityBadge } from "@/components/StatusBadge";
import { ResourceForm } from "@/components/ResourcePage";
import { Button } from "@/components/ui/button";
import { TASK_STATUSES, PRIORITIES, fmtDate, daysUntil } from "@/lib/constants";
import { useEventOptions } from "@/hooks/useEventOptions";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, Calendar, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const COL_STYLE = {
  "TODO": "border-t-amber-400",
  "IN PROGRESS": "border-t-blue-500",
  "REVIEW": "border-t-violet-500",
  "DONE": "border-t-emerald-500",
};

export default function Tasks() {
  const { can } = useAuth();
  const { events } = useEventOptions();
  const [eventId, setEventId] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [masterTeam, setMasterTeam] = useState([]);
  useEffect(() => { list("team_members").then(setMasterTeam).catch(() => {}); }, []);

  const load = () => {
    const params = eventId !== "all" ? { event_id: eventId } : {};
    list("tasks", params).then(setTasks).catch(() => setTasks([]));
  };
  useEffect(load, [eventId]); // eslint-disable-line

  const eventName = (id) => { const e = events.find((x) => x.id === id); return e ? `${e.groom} & ${e.bride}` : ""; };

  const moveTask = async (task, status) => {
    if (task.status === status) return;
    setTasks((p) => p.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try { await update("tasks", task.id, { status }); } catch { toast.error("Gagal memindahkan task"); load(); }
  };

  const del = async (id) => { await remove("tasks", id); toast.success("Task dihapus"); load(); };

  const fields = [
    { name: "name", label: "Nama Task", required: true, full: true },
    { name: "event_id", label: "Event", type: "select", options: events.map((e) => ({ value: e.id, label: `${e.groom} & ${e.bride}` })), full: true },
    { name: "pic", label: "PIC 1", type: "select", options: masterTeam.map((m) => ({ value: m.name, label: m.role ? `${m.name} — ${m.role}` : m.name })) },
    { name: "pic2", label: "PIC 2", type: "select", options: masterTeam.map((m) => ({ value: m.name, label: m.role ? `${m.name} — ${m.role}` : m.name })) },
    { name: "priority", label: "Priority", type: "select", options: PRIORITIES, default: "MEDIUM" },
    { name: "status", label: "Status", type: "select", options: TASK_STATUSES, default: "TODO" },
    { name: "deadline", label: "Deadline", type: "date" },
    { name: "notes", label: "Catatan", type: "textarea", full: true },
  ];

  return (
    <div>
      <PageHeader overline="Papan Activity" title="Activity" subtitle="Kelola tugas & checklist tim dengan alur TODO → In Progress → Review → Done.">
        {can(2) && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="tasks-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Activity Baru</Button>}
      </PageHeader>

      <div className="mb-4"><EventSelect value={eventId} onChange={setEventId} /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {TASK_STATUSES.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col);
          return (
            <div
              key={col}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { const t = tasks.find((x) => x.id === dragId); if (t) moveTask(t, col); setDragId(null); }}
              className={cn("bg-beige/60 rounded-xl p-3 border-t-4", COL_STYLE[col])}
              data-testid={`kanban-col-${col.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">{col}</h3>
                <span className="text-xs font-semibold text-[#7A7A7A] bg-white rounded-full px-2 py-0.5">{colTasks.length}</span>
              </div>
              <div className="space-y-2.5 min-h-[100px]">
                {colTasks.map((t) => {
                  const d = daysUntil(t.deadline);
                  const overdue = d != null && d < 0 && t.status !== "DONE";
                  return (
                    <div
                      key={t.id}
                      draggable={can(2)}
                      onDragStart={() => setDragId(t.id)}
                      className="bg-white rounded-lg border border-gold/20 p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing group"
                      data-testid="kanban-card"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-[#1A1A1A] leading-snug">{t.name}</p>
                        {can(2) && (
                          <div className="flex opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <button onClick={() => { setEditing(t); setFormOpen(true); }} className="p-1 text-[#7A7A7A] hover:text-burgundy" data-testid="task-edit"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => del(t.id)} className="p-1 text-[#7A7A7A] hover:text-red-600" data-testid="task-delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                      {eventId === "all" && t.event_id && <p className="text-[10px] text-gold-dark mt-1">{eventName(t.event_id)}</p>}
                      <div className="flex items-center flex-wrap gap-2 mt-2.5">
                        <PriorityBadge priority={t.priority} />
                        {t.deadline && <span className={cn("flex items-center gap-1 text-[10px]", overdue ? "text-red-600 font-semibold" : "text-[#7A7A7A]")}><Calendar className="w-3 h-3" />{overdue ? "OVERDUE" : fmtDate(t.deadline)}</span>}
                      </div>
                      {(t.pic || t.pic2) && <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-2 text-[11px] text-[#4A4A4A]"><User className="w-3 h-3" />{[t.pic, t.pic2].filter(Boolean).join(", ")}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={editing} resource="tasks" onSaved={load} defaults={eventId !== "all" ? { event_id: eventId } : {}} title={editing ? "Edit Task" : "Task Baru"} />
    </div>
  );
}
